import { defineMessages } from 'react-intl'
import Fuse from 'fuse.js'

import config from 'src/config'
import countries from 'src/json/countries.json'
import pricelists from 'src/json/pricelists.json'
import categories from 'src/json/categories.json'
import products from 'src/json/products.json'
import { warn } from 'src/exception'

import { getBrandById } from './brand'
import { getLocalizedCategoryData } from './category'

type Price = null | {
  price: string
  pricelist: {
    countries: string[]
    currency: {
      currency: string
      decimalDigits: string
      decimalPoints: string
    }
  }
}

const find = (arr: any[], key: string, value: any) =>
  arr
    ? arr.filter((entry: any) => entry[key] === value).slice(0)[0] || null
    : null

const messages = defineMessages({
  description: {
    id: 'productInfo.description',
    defaultMessage: 'Översikt',
  },
  infoDetails: {
    id: 'productInfo.details',
    defaultMessage: 'Detaljer',
  },
  infoCareGuide: {
    id: 'productInfo.careguide',
    defaultMessage: 'Vårdguide',
  },
  infoFitSize: {
    id: 'productInfo.fit&Size',
    defaultMessage: 'Passform & Storlek',
  },
  shippingInfo: {
    id: 'productInfo.shipping',
    defaultMessage: 'Leverans',
  },
})

export function getLocalizedProductData(product: any, language: string): any {
  const centraCode =
    config.locales && config.locales[language]
      ? config.locales[language].centraCode
      : null
  if (
    centraCode &&
    product &&
    product.localized &&
    product.localized[centraCode]
  ) {
    return {
      ...product,
      ...product.localized[centraCode],
    }
  } else {
    return {
      ...product,
    }
  }
}

export function isUnavailable(productMarkets: any, country: string): boolean {
  if (country) {
    const countryData = find(countries, 'country', country)

    if (countryData) {
      const { market } = countryData

      const productMarket = find(productMarkets, 'market', market)

      if (productMarket) {
        return true
      }
    }
  }
  return false
}

export function getProductsByBrand(brand: string): object[] {
  return products.filter((product: any) => brand === product.brand)
}

export function getProductsByIds(productIds: string[]): object[] {
  return products.filter((product: any) => productIds.includes(product.product))
}

const searchOptionsSV = {
  shouldSort: true,
  threshold: 0.5,
  minMatchCharLength: 3,
  keys: [
    {
      name: 'localized.sv.infoColorSwatch.display_name',
      weight: 0.5,
    },
    {
      name: 'name',
      weight: 0.5,
    },
    {
      name: 'localized.sv.categories.name',
      weight: 0.5,
    },
    {
      name: 'brandName',
      weight: 0.5,
    },
  ],
}

const searchOptionsNO = {
  shouldSort: true,
  threshold: 0.5,
  minMatchCharLength: 3,
  keys: [
    {
      name: 'localized.no.infoColorSwatch.display_name',
      weight: 0.5,
    },
    {
      name: 'name',
      weight: 0.5,
    },
    {
      name: 'localized.no.categories.name',
      weight: 0.5,
    },
    {
      name: 'brandName',
      weight: 0.5,
    },
  ],
}

const fuseSearchSV = new Fuse(products, searchOptionsSV)
const fuseSearchNO = new Fuse(products, searchOptionsNO)

export function searchProducts(query: string, locale: string) {
  query = query.toLocaleLowerCase()
  let result = []
  if (locale === 'sv') {
    result = fuseSearchSV.search(query)
  } else if (locale === 'nb') {
    result = fuseSearchNO.search(query)
  }
  return result.slice(0, 32).filter(product => getBrandById(product.brand).slug)
}

export function getProductsByIdsAndBrand(productIds: string[], brand: string) {
  const response = productIds
    .map((productId: string) => {
      const selectedProduct = products.find((product: any) => {
        if (productId === product.product && product.brand === brand) {
          return true
        } else {
          return false
        }
      })
      if (selectedProduct) {
        return selectedProduct
      } else {
        return false
      }
    })
    .filter(product => product)

  return response
}

export function getProductById(productId: string): any {
  return products.filter((product: any) => productId === product.product)[0]
}

export function isItemAvailable(country: string, stock: any) {
  if (country) {
    const countryData = find(countries, 'country', country)
    if (countryData) {
      const { market } = countryData

      if ((stock[market] && stock[market] > 0) || stock === 'yes') {
        return true
      }
    }
  }
  return false
}

export function getCategoryByProduct(product: any, location: string) {
  const { canonicalCategory } = product
  let category = null
  if (canonicalCategory) {
    Object.keys(categories).forEach(key => {
      if (parseInt(categories[key].category) === canonicalCategory) {
        const { name } = getLocalizedCategoryData(categories[key], location)
        category = name ? name : categories[key].name
      }
    })
  }
  return category || ''
}

export function getProductComponentData(
  product: any,
  productInfo: any,
  location: string
) {
  const { description, name, excerpt } = getLocalizedProductData(
    product,
    location
  )
  const { canonicalCategory, infoCareGuide, infoFitSize, infoDetails } = product

  const brand = getBrandById(product.brand)
  const { shippingInfo } = productInfo

  let bredcrumbsItems = []
  let category = null
  Object.keys(categories).forEach(key => {
    if (parseInt(categories[key].category) === canonicalCategory) {
      const { name } = getLocalizedCategoryData(categories[key], location)
      category = {
        link: `${brand.slug}/${categories[key].completeUri}`,
        name: name ? name : categories[key].name,
      }
    }
  })

  // Push brand to breadcrumbs
  bredcrumbsItems.push({
    link: brand.slug,
    name: brand.brand,
  })
  // Push category to breadcrumbs
  if (category) {
    bredcrumbsItems.push(category)
  }
  // Push product to breadcrumbs
  bredcrumbsItems.push({ name: name })

  const tabInfo = []
  if (description) {
    tabInfo.push({
      isOpen: true,
      text: description,
      message: messages.description,
    })
  }
  if (excerpt) {
    tabInfo.push({
      text: excerpt,
      message: messages.infoDetails,
    })
  }
  if (infoCareGuide) {
    tabInfo.push({
      text: infoCareGuide,
      message: messages.infoCareGuide,
    })
  }
  if (infoFitSize) {
    tabInfo.push({
      text: infoFitSize,
      message: messages.infoFitSize,
    })
  }
  if (shippingInfo) {
    tabInfo.push({
      richText: shippingInfo,
      // text: shippingInfo,
      message: messages.shippingInfo,
    })
  }

  return {
    tabInfo,
    bredcrumbsItems,
  }
}

export function getRelatedProducts(product: any) {
  const { relatedProducts } = product
  let related = null
  const relatedIds = relatedProducts
    ? relatedProducts.map((item: any) => item.product)
    : null
  if (relatedIds) {
    relatedIds.push(product.product)
    relatedIds.sort()

    related = getProductsByIds(relatedIds)
  }
  return related
}

export function getPrice(productMarkets: any, country: string): Price {
  const countryData = find(countries, 'country', country)
  if (!countryData) {
    warn(new Error(`Country ${country} does not exist`))
  } else {
    const { market, pricelist } = countryData
    const productMarket = find(productMarkets, 'market', market)
    if (!productMarket) {
      warn(new Error(`Product is unavailable in market ${market}`))
    } else {
      const { pricesByPricelist } = productMarket
      let productPrice = find(pricesByPricelist, 'pricelist', pricelist)
      if (!productPrice) {
        warn(new Error(`Product has no price in pricelist ${pricelist}`))
      } else {
        const pricelistData = find(pricelists, 'pricelist', pricelist)
        if (!pricelistData) {
          warn(new Error(`Pricelist ${pricelist} not found`))
        } else {
          productPrice = Object.assign({}, productPrice, {
            meta: pricelistData,
          })
        }
        return productPrice
      }
    }
  }
  return null
}
